import {
  GreyGrey60,
  GreyGrey70,
  IconCheckmark,
  IconEdit,
  IconTrashCan,
  IndigoIndigo60,
  Primary12SemiBold,
  Primary13Regular,
  RedRed50,
  RedRed60,
  SequenceMenuItemType
} from '@sequencehq/core-components'
import { MinimumRow, PricingRow } from './usePricingTable'
import { CopyToPhase } from './useCopyPriceToPhase'

const styles = {
  tableHeader: {
    ...Primary12SemiBold,
    color: GreyGrey60,
    textTransform: 'uppercase' as const,
    border: 'none',
    padding: '10px 8px'
  },
  tableCell: {
    alignItems: 'center',
    ...Primary13Regular,
    border: 'none',
    textAlign: 'left' as const,
    padding: '8px'
  },
  editIcon: {
    width: '16px',
    height: '16px',
    color: IndigoIndigo60
  },
  deleteIcon: {
    width: '16px',
    height: '16px',
    color: RedRed50
  },
  error: {
    ...Primary13Regular,
    color: RedRed60,
    padding: '8px 0 0',
    width: '280px'
  },
  tooltip: {
    ...Primary13Regular,
    color: GreyGrey70,
    lineHeight: '20px'
  }
}

/**
 * The menu reads in this order regardless of how the row lists its features,
 * with the removals grouped at the end.
 */
const PRODUCT_ROW_ACTION_ORDER: Array<keyof PricingRow['availableFeatures']> = [
  'canEdit',
  'canCopyPrice',
  'canEditPriceMetadata',
  'canEditDiscount',
  'canDeleteDiscount',
  'canDelete'
]

export const productRowActions = (row: PricingRow): SequenceMenuItemType[] => {
  const allActions: Record<
    keyof PricingRow['availableFeatures'],
    SequenceMenuItemType
  > = {
    canEdit: {
      isDisabled: false,
      label: row.availableFeatures.canEdit?.disabled
        ? 'View price'
        : 'Edit price',
      tooltip: row.availableFeatures.canEdit?.disabledReason,
      onClick: row.availableFeatures.canEdit?.handler,
      'data-testid': `cube.priceRow.${row.id}.menu.editPrice`
    },
    canDelete: {
      isDisabled: row.availableFeatures.canDelete?.disabled,
      label: 'Remove product',
      tooltip: row.availableFeatures.canDelete?.disabledReason,
      onClick: row.availableFeatures.canDelete?.handler,
      style: { color: RedRed60 },
      'data-testid': `cube.priceRow.${row.id}.menu.removeProduct`
    },
    canCopyPrice: copyToPhaseMenuItem({
      copyToPhase: row.availableFeatures.canCopyPrice,
      'data-testid': `cube.priceRow.${row.id}.menu.copyPrice`
    }),
    canEditDiscount: {
      isDisabled: row.availableFeatures.canEditDiscount?.disabled,
      label: 'Edit discount',
      tooltip: row.availableFeatures.canEditDiscount?.disabledReason,
      onClick: row.availableFeatures.canEditDiscount?.handler,
      'data-testid': `cube.priceRow.${row.id}.menu.editDiscount`
    },
    canDeleteDiscount: {
      isDisabled: row.availableFeatures.canDeleteDiscount?.disabled,
      label: 'Remove discount',
      tooltip: row.availableFeatures.canDeleteDiscount?.disabledReason,
      onClick: row.availableFeatures.canDeleteDiscount?.handler ?? (() => null),
      style: { color: RedRed60 },
      'data-testid': `cube.priceRow.${row.id}.menu.removeDiscount`
    },
    canEditPriceMetadata: {
      isDisabled: row.availableFeatures.canEditPriceMetadata?.disabled,
      label: 'ARR Calculation',
      tooltip: row.availableFeatures.canEditPriceMetadata?.disabledReason,
      hidden: row.availableFeatures.canEditPriceMetadata?.hidden,
      subItems: [
        {
          label: 'Include (default)',
          iconRight:
            row.phasePriceMetadata?.arrCalculation !== 'EXCLUDE' ? (
              <IconCheckmark />
            ) : undefined,
          onClick: () =>
            row.availableFeatures.canEditPriceMetadata?.handler({
              arrCalculation: 'INCLUDE'
            })
        },
        {
          label: 'Exclude',
          iconRight:
            row.phasePriceMetadata?.arrCalculation === 'EXCLUDE' ? (
              <IconCheckmark />
            ) : undefined,
          onClick: () =>
            row.availableFeatures.canEditPriceMetadata?.handler({
              arrCalculation: 'EXCLUDE'
            })
        }
      ]
    }
  }

  return PRODUCT_ROW_ACTION_ORDER.filter(
    actionName => actionName in row.availableFeatures
  ).map(actionName => allActions[actionName])
}

export const minimumRowActions = (row: MinimumRow): SequenceMenuItemType[] => {
  return [
    {
      isDisabled: !row.actions.edit.available,
      label: 'Edit minimum',
      onClick: row.actions.edit.handler,
      iconLeft: <IconEdit {...styles.editIcon} />,
      'data-testid': `cube.minimumRow.${row.id}.menu.edit`
    },
    {
      isDisabled: !row.actions.delete.available,
      label: 'Remove minimum',
      onClick: row.actions.delete.handler,
      iconLeft: <IconTrashCan {...styles.deleteIcon} />,
      'data-testid': `cube.minimumRow.${row.id}.menu.remove`
    }
  ]
}

/**
 * Every other phase sits in a sub-menu, each saying why it cannot take the copy
 * where that is the case. A row with no other phase has no item at all.
 */
const copyToPhaseMenuItem = (args: {
  copyToPhase?: CopyToPhase
  'data-testid': string
}): SequenceMenuItemType => {
  const label = 'Copy price to'
  const targets = args.copyToPhase?.targets ?? []

  if (!targets.length) {
    return { label, hidden: true }
  }

  return {
    label,
    'data-testid': args['data-testid'],
    subItems: targets.map(target => ({
      label: target.label,
      isDisabled: Boolean(target.disabledReason),
      tooltip: target.disabledReason,
      onClick: () => args.copyToPhase?.copyTo(target.id),
      'data-testid': `${args['data-testid']}.${target.id}`
    }))
  }
}
